import BEMHelper from '@ynap/bem-helper';
import { Button } from '@ynap/button';
import Notification from '@ynap/notification';
import { PriceWithPromotions as Price } from '@ynap/price';
import PropTypes from 'prop-types';
import React from 'react';

const bem = new BEMHelper('Totals');

const propTypes = {
    className: PropTypes.string,
    promotions: PropTypes.array,
    itemTotal: PropTypes.string,
    originalShippingPrice: PropTypes.string,
    finalShippingPrice: PropTypes.string,
    packaging: PropTypes.string,
    total: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    children: PropTypes.node,
    removePromotion: PropTypes.func,
    rewards: PropTypes.array,
    messages: PropTypes.shape({
        orderReview: PropTypes.shape({
            DDPTitle: PropTypes.func,
        }),
        totals: PropTypes.shape({
            itemTotal: PropTypes.func,
            cashOnDelivery: PropTypes.func,
            shipping: PropTypes.func,
            packaging: PropTypes.func,
            total: PropTypes.string.func,
            remove: PropTypes.func,
            removeButtonLabel: PropTypes.func,
            savingsExplanation: PropTypes.func,
            rewardsTitle: PropTypes.func,
        }),
    }).isRequired,
    tax: PropTypes.string,
    duty: PropTypes.string,
    taxDutyInfo: PropTypes.string,
    noGrandTotalBorder: PropTypes.bool,
    storeCredit: PropTypes.string,
    savingsTotal: PropTypes.string,
    shippingTax: PropTypes.string,
    orderTaxes: PropTypes.object,
    enablePromotions: PropTypes.bool,
    codChargeAmount: PropTypes.string,
    ddpInfo: PropTypes.bool,
    showUsTaxUi: PropTypes.bool,
    isCheckoutLayout: PropTypes.bool,
    priceWithTax: PropTypes.object,
    defaultShippingOption: PropTypes.object,
    variables: PropTypes.object,
    selectedShippingOption: PropTypes.object,
    displaySavingsExplanation: PropTypes.bool,
};

function Totals(props) {
    const {
        className,
        itemTotal,
        originalShippingPrice,
        finalShippingPrice,
        packaging,
        total,
        currency,
        promotions,
        removePromotion,
        rewards = [],
        messages,
        children,
        tax,
        duty,
        taxDutyInfo,
        noGrandTotalBorder,
        storeCredit,
        savingsTotal,
        shippingTax,
        orderTaxes,
        enablePromotions,
        codChargeAmount,
        ddpInfo,
        priceWithTax,
        showUsTaxUi = false,
        isCheckoutLayout,
        selectedShippingOption = null,
        displaySavingsExplanation,
    } = props;
    
    const containsFreeShippingReward = rewards.some(reward => reward.isFreeShipping)

    return (
        <div className={bem(null, null, className)}>
            <ul className={bem('orderSummary')}>
                {itemTotal && !showUsTaxUi ? (
                    <li className={bem('item', 'itemTotal')}>
                        <span className={bem('description')}>{messages.totals.itemTotal()}</span>
                        <span className={bem('amount')}>{itemTotal}</span>
                    </li>
                ) : null}

                {showUsTaxUi && priceWithTax ? (
                    <li className={bem('item', 'itemTotal')}>
                        <span className={bem('description')}>{messages.totals.itemTotal()}</span>
                        <span className={bem('amount')}>{priceWithTax}</span>
                    </li>
                ) : null}
                {(originalShippingPrice || finalShippingPrice) && !!selectedShippingOption && !containsFreeShippingReward ? (
                    <li className={bem('item', 'shipping')}>
                        <span className={bem('description')}>
                            {messages.totals.shipping()}
                        </span>
                        <Price className={bem('price')} markedDownPrice={originalShippingPrice} finalPrice={finalShippingPrice} shipping={true} />
                    </li>
                ) : null}
                {codChargeAmount ? (
                    <li className={bem('item', 'cod')}>
                        <span className={bem('description')}>{messages.totals.cashOnDelivery()}</span>
                        <span className={bem('amount')}>{codChargeAmount}</span>
                    </li>
                ) : null}
                {storeCredit ? (
                    <li className={bem('item', 'storeCredit')}>
                        <span className={bem('description')}>{messages.totals.storeCreditLabel()}</span>
                        <span className={bem('amount')}>{messages.totals.storeCredit({ storeCredit })}</span>
                    </li>
                ) : null}
                {tax && !showUsTaxUi ? (
                    <li className={bem('item', 'tax')}>
                        <span className={bem('description')}>{messages.totals.tax()}</span>
                        <span className={bem('amount')}>{tax}</span>
                    </li>
                ) : null}
                {duty ? (
                    <li className={bem('item', 'duty')}>
                        <span className={bem('description')}>{messages.totals.duty()}</span>
                        <span className={bem('amount')}>{duty}</span>
                    </li>
                ) : null}
                {shippingTax ? (
                    <li className={bem('item', 'shippingTax')}>
                        <span className={bem('description')}>{messages.totals.shippingTax()}</span>
                        <span className={bem('amount')}>{shippingTax}</span>
                    </li>
                ) : null}
                {packaging ? (
                    <li className={bem('item', 'packaging')}>
                        <span className={bem('description')}>{messages.totals.packaging()}</span>
                        <span className={bem('amount')}>{packaging}</span>
                    </li>
                ) : null}
                {enablePromotions && promotions && promotions.length > 0
                    ? promotions.map((promo) => {
                        // A promotion can also be a reward in which case it will be listed in the rewards section instead 
                        return promo.isExclusive ? null : <li className={bem('item', 'promotion')} key={promo.code}>
                            <span className={bem('description', 'promotion')}>
                                <span className={bem('promotionCode')}>{promo.code}</span>
                                <span className={bem('promotionDescription')}>{promo.description}</span>
                                {removePromotion ? (
                                    <Button
                                        className={bem('remove')}
                                        aria-label={messages.totals.removeButtonLabel()}
                                        onClick={() => removePromotion(promo.code)}
                                    >
                                        {messages.totals.remove && messages.totals.remove()}
                                    </Button>
                                ) : null}
                            </span>
                            <span className={bem('promotionName')}>{promo.description}</span>
                        </li>
                    })
                    : null}
                {rewards && rewards.length > 0 ? (
                    <li className={bem('item', 'rewards')} key="rewards">
                        <span className={bem('rewardsTitle')}>{messages.totals.rewardsTitle()}</span>
                        <ul className={bem('rewardsList')}>
                            {rewards.map((reward) => (
                                <li className={bem('item', 'reward')} key={reward.code}>
                                    <span className={bem('rewardName')}>{reward.description}</span>
                                    <span className={bem('rewardValue')}>{reward.amount}</span>
                                </li>
                            ))}
                        </ul>
                    </li>
                ) : null}
                {orderTaxes?.BROKERAGE_FEE ? (
                    <li className={bem('item', 'tax')}>
                        <span className={bem('description')}>{messages.totals.brokerage()}</span>
                        <span className={bem('amount')}>{orderTaxes.BROKERAGE_FEE}</span>
                    </li>
                ) : null}
                {children ? <li className={bem('item')}>{children}</li> : null}
                <li
                    className={bem('item', {
                        total: true,
                        noGrandTotalBorder,
                        ddpInfo,
                        isCheckoutLayout,
                    })}
                >
                    <span className={bem('description')}>
                        <span className={bem('total')}>{messages.totals.total()}</span>
                        <span className={bem('currency')}>({currency})</span>
                    </span>
                    <span className={bem('amountTotal')}>{total}</span>
                </li>
            </ul>
            {messages.totals.savingsTotal && savingsTotal ? (
                <div className={bem('savingsDetails')}>
                    <div className={bem('savingsTotal')}>
                        {messages.totals.savingsTotal()}
                        <span className={bem('savingsTotal', 'amount')}>{savingsTotal}</span>
                    </div>
                    {displaySavingsExplanation && <div className={bem('savingsExplanation')}>{messages.totals.savingsExplanation()}</div>}
                </div>
            ) : null}

            {taxDutyInfo ? <Notification className={bem('info')} type="info" message={taxDutyInfo} /> : null}

            {messages.orderReview?.DDPTitle && ddpInfo && <div className={bem('ddpInfo')}>{messages.orderReview.DDPTitle()}</div>}
        </div>
    );
}

Totals.propTypes = propTypes;

export default Totals;
